import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Building2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/Button.tsx';
import { Input } from '@/components/Input.tsx';
import { useAuth } from '@/hooks/useAuth.ts';
import type { ApiError } from '@/lib/api.ts';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { createTenant, user } = useAuth();

  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await createTenant({ name });
      navigate('/', { replace: true });
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to create workspace. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Box className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="mt-1 text-center text-sm text-foreground-muted">
            Let's create your first workspace to organize your 3D printing inventory.
          </p>
        </div>

        {/* Form */}
        <div className="rounded-lg border border-border bg-background-subtle p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 rounded-md bg-error/10 p-3 text-sm text-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Input
              label="Workspace name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Print Shop"
              leftIcon={<Building2 className="h-4 w-4" />}
              hint="This is how your workspace will appear in the app"
              required
              autoFocus
            />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={isSubmitting}
            >
              Create workspace
            </Button>
          </form>
        </div>

        {/* Info */}
        <p className="mt-6 text-center text-xs text-foreground-subtle">
          You can create additional workspaces later or invite team members
          to collaborate with you.
        </p>
      </div>
    </div>
  );
}
